import { theme } from 'antd';
import { invitedStatus } from 'config/invitedStatus';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedUsers } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { useManagePermittedRoles, useModuleSettings } from 'library/hooks';
import { AddPeopleForm } from 'zComponents/atoms';
import { Button, Col, Divider, Flex, Modal, Row, Segmented, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

//✅ List Segmented
const BASE_OPTIONS_SEGMENTED = ['Zezamii Users', 'Access-only Users'];

const AddPeopleModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	//✅ Hooks
	const {
		token: { fontSizeSM, paddingXL, paddingSM, colorTextDescription, colorBorder }
	} = theme.useToken();
	const dispatch = useDispatch();
	const enabledInnerRangeModule = useModuleSettings(CONSTANTS_SETTINGS.INNER_RANGE_MODULE);

	//✅ Redux State
	const { loadingAddProfile } = useSelector((state) => state.People);
	const { completeAddMembersTeam } = useSelector((state) => state.Teams);
	const selectedUsers = useSelector(selectSelectedUsers);

	//✅ Memoized options for segmented control based on module settings
	const optionsSegmented = useMemo(() => {
		return enabledInnerRangeModule ? BASE_OPTIONS_SEGMENTED : [BASE_OPTIONS_SEGMENTED[0]];
	}, [enabledInnerRangeModule]);

	//✅ Local State
	const [selectedSegmented, setSelectedSegmented] = useState(optionsSegmented[0]);
	const [internalVisible, setInternalVisible] = useState(false);

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	//✅ Permissions
	const canAddPerson = useManagePermittedRoles('addPerson');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const handleSelectedSegmented = (value) => {
		setSelectedSegmented(value);
		dispatch(peopleActions.clearSelectedUsers());
	};

	useEffect(() => {
		if (!optionsSegmented.includes(selectedSegmented)) {
			setSelectedSegmented(optionsSegmented[0]);
			dispatch(peopleActions.clearSelectedUsers());
		}
	}, [optionsSegmented, selectedSegmented, dispatch]);

	//✅ Function to handle cancel
	const handleCancel = useCallback(() => {
		setVisible(false);
		dispatch(peopleActions.clearSelectedUsers());
	}, [setVisible, dispatch]);

	useEffect(() => {
		// This was causing the modal to close automatically before user could see the completion state
		return;
	}, [selectedUsers, handleCancel]);

	// Handle team members addition completion
	useEffect(() => {
		if (completeAddMembersTeam) {
			setTimeout(() => {
				handleCancel();
			}, 500);
		}
	}, [completeAddMembersTeam, handleCancel, visible]);

	//✅ Function to invite people using Redux
	const handleInvitePeople = () => {
		dispatch(peopleActions.invitePeople());
	};

	const handleInviteAccessOnlyPeople = async () => {
		dispatch(peopleActions.inviteAccessOnlyPeople());
	};

	const getTitleText = () => {
		return selectedSegmented === 'Zezamii Users' ? 'Invite People to Organisation' : 'Add People to Organisation';
	};
	const getDescriptionText = () => {
		return selectedSegmented === 'Zezamii Users'
			? 'Sending people an email invite gives them access to the Zezamii sign-up link and adds them to the Zezamii Platform as Zezamii Users.'
			: "Adding people's names adds them to the Zezamii Platform as Access-only Users.";
	};

	const getTooltipMessage = () => {
		if (selectedUsers.length === 0) {
			return selectedSegmented === 'Zezamii Users'
				? 'You must invite at least 1 user before proceeding.'
				: 'You must add at least 1 user before proceeding.';
		}

		if (
			selectedSegmented === 'Zezamii Users' &&
			selectedUsers.some((user) => user.status === invitedStatus.PENDING && user.type === 'email')
		) {
			return 'You must invite all users selected before proceeding.';
		}

		return '';
	};

	// Check if all users are successfully invited/added
	const allUsersCompleted =
		selectedUsers.length > 0 && selectedUsers.every((user) => user.status === invitedStatus.INVITED);

	// Check if there are pending users that need to be processed
	const hasPendingUsers = selectedUsers.some((user) => user.status === invitedStatus.PENDING);

	const getButtonText = () => {
		if (allUsersCompleted) {
			return 'Complete';
		}

		if (loadingAddProfile) {
			return selectedSegmented === BASE_OPTIONS_SEGMENTED[0] ? 'Sending Invites...' : 'Adding People...';
		}

		return selectedSegmented === BASE_OPTIONS_SEGMENTED[0] ? 'Send Invites' : 'Add People';
	};

	return (
		<>
			<Tooltip title={!canAddPerson ? permissionDeniedMessage : 'Add People'} placement='top'>
				<span onClick={(e) => canAddPerson && setVisible(true)}>
					{children ?? (
						<Button type='primary' disabled={!canAddPerson}>
							Add People
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				centered
				closeIcon
				destroyOnClose={true}
				width={859}
				styles={{
					content: { padding: paddingXL }
				}}
				footer={[
					<>
						<div style={{ margin: `0px -${paddingXL}px` }}>
							<Divider style={{ margin: `${paddingXL}px 0` }} />
						</div>
						<Button onClick={() => handleCancel()} disabled={loadingAddProfile}>
							Cancel
						</Button>
						<Tooltip key='next' title={getTooltipMessage()} color='gold'>
							<Button
								type='primary'
								onClick={
									allUsersCompleted
										? handleCancel
										: selectedSegmented === BASE_OPTIONS_SEGMENTED[0]
											? handleInvitePeople
											: handleInviteAccessOnlyPeople
								}
								loading={loadingAddProfile}
								disabled={
									selectedUsers.length === 0 ||
									(hasPendingUsers && loadingAddProfile) ||
									(!hasPendingUsers && !allUsersCompleted)
								}
							>
								{getButtonText()}
							</Button>
						</Tooltip>
					</>
				]}
			>
				<Row gutter={[0, paddingXL]}>
					<Col span={24}>
						<Flex vertical>
							<Title level={4} style={{ margin: 0, lineHeight: '28px' }}>
								{getTitleText()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									color: colorTextDescription,
									fontWeight: 400,
									lineHeight: '20px',
									marginBottom: paddingSM,
									whiteSpace: 'pre-line',
									minHeight: 40
								}}
							>
								{getDescriptionText()}
							</Text>
						</Flex>
						<div style={{ margin: `0px -${paddingXL}px` }}>
							<Divider style={{ margin: `10px 0px`, color: colorBorder }} />
						</div>
					</Col>

					<Col span={24}>
						<Flex gap={paddingXL} vertical>
							{/* ✅ Only show selection if Inner Range is enabled */}
							{enabledInnerRangeModule && (
								<Flex justify='space-between' style={{ width: '100%' }}>
									<Segmented
										options={optionsSegmented}
										value={selectedSegmented}
										block
										onChange={handleSelectedSegmented}
										style={{ width: '100%' }} // Full width for the segmented control
									/>
								</Flex>
							)}
							<AddPeopleForm selectedSegmented={selectedSegmented} />
						</Flex>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default AddPeopleModal;
